import React, { useState, useEffect } from "react";
import {
  Box, VStack, Heading, Text, useColorModeValue, Button,
  HStack, useBreakpointValue, useToast, Spinner, Divider, Tooltip,
  SimpleGrid,
} from "@chakra-ui/react";
import { FaChartPie, FaListUl, FaArrowLeft, FaCalendarAlt, FaTrophy } from "react-icons/fa";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useStudentContext } from "../../Student/B2CStudentContext"
import {
  PracticeAttemptDetails,
  PracticeExamAttemptScore,
  QuestionSetPracticeAttemptScore,
  PracticeExamAttemptAnalytics,
  PracticeQuestionSetAttemptAnalytics,
} from "../../../Platform/Common/types";
import B2CQuestionSetViewAttempt from "../Components/B2CQuestionSetViewAttempt";
import B2CScoreViewer from "../Components/B2CScoreViewer";
import B2CQsetScoreViewer from "../Components/B2CQsetScoreViewer";
import B2CQuestionSetAnalytics from "../Components/B2CQuestionSetAnalytics";

const PracticeAttemptDetailsViewer = ({ isAdminView = false }: { isAdminView?: boolean }) => {

  const location = useLocation();
  const [examAttemptScore, setExamAttemptScore] = useState<PracticeExamAttemptScore | null>(null);
  const [qsetAttemptScore, setQsetAttemptScore] = useState<QuestionSetPracticeAttemptScore | null>(null);

  const [examAttemptAnalytics, setExamAttemptAnalytics] = useState<PracticeExamAttemptAnalytics | null>(null);
  const [qsetAttemptAnalytics, setQsetAttemptAnalytics] = useState<PracticeQuestionSetAttemptAnalytics | null>(null);

  const [examAttemptDetails, setExamAttemptDetails] = useState<PracticeAttemptDetails>();
  const [attemptContext, setAttemptContext] = useState<any>(null);

  const [assessmentType, setAssessmentType] = useState<"MOCK_EXAM" | "QUESTION_SET" | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [viewMode, setViewMode] = useState<"score" | "analytics" | "attempt">("score");

  const { practice_attempt_id } = useParams<{ practice_attempt_id: string }>();
  const token = localStorage.getItem("b2c_token");
  const isAuthenticated = Boolean(token);
  const navigate = useNavigate();
  const toast = useToast();
  const { studentContext } = useStudentContext();

  const textColor = useColorModeValue("blue.800", "blue.50");
  const cardBgDefault = useColorModeValue("gray.100", "gray.700");
  const cardBgHover = useColorModeValue("gray.200", "gray.600");
  const cardTextColor = useColorModeValue("gray.800", "gray.100");
  const cardBorderColor = useColorModeValue("gray.300", "gray.600");
  const heroBg = useColorModeValue("pink.50", "pink.900");
  const heroBorder = useColorModeValue("blue.100", "blue.800");
  const heroMutedText = useColorModeValue("gray.600", "gray.400");

  const [scorePending, setScorePending] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [pendingStatus, setPendingStatus] = useState<"MANUAL_SCORING_PENDING" | "AUTO_SCORING_PENDING" | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

  const fetchExamAttemptDetails = async (switchToAttemptView: boolean = false) => {
    if (examAttemptDetails) {
      if (switchToAttemptView) setViewMode("attempt");
      return examAttemptDetails;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(
        `${apiUrl}/b2c/practice-attempt/${practice_attempt_id}/post-attempt-details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExamAttemptDetails(response.data);
      setAssessmentType(response.data.assessment_type);
      if (switchToAttemptView) setViewMode("attempt");
      return response.data as PracticeAttemptDetails;
    } catch (error) {
      toast({ title: "Failed to fetch detailed attempt", status: "error", duration: 3000, isClosable: true });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExamScore = async (type: "MOCK_EXAM" | "QUESTION_SET") => {
    try {
      setIsLoading(true);

      const endpoint = type === "MOCK_EXAM"
        ? `${apiUrl}/b2c/practice-exam-attempt/${practice_attempt_id}/score`
        : `${apiUrl}/b2c/practice-qset-attempt/${practice_attempt_id}/score`;

      const response = await axios.get(endpoint, { headers: { Authorization: `Bearer ${token}` } });

      if (type === "MOCK_EXAM") setExamAttemptScore(response.data);
      else setQsetAttemptScore(response.data);

      setScorePending(false);
      setPendingMessage(null);
      return false;

    } catch (error: any) {
      const detail = error.response?.data?.detail;

      if (error.response?.status === 409 && detail) {
        setScorePending(true);
        setPendingMessage(detail.message);
        setPendingStatus(detail.status ?? null);

        if (type === "MOCK_EXAM") setExamAttemptScore(null);
        else setQsetAttemptScore(null);

        if (detail.status === "AUTO_SCORING_PENDING") {
          toast({
            title: "Scoring interrupted",
            description: detail.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }

        return true;
      } else {
        toast({ title: "Failed to fetch score", status: "error", duration: 3000, isClosable: true });
        return true;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExamAnalytics = async (type: "MOCK_EXAM" | "QUESTION_SET") => {
    try {
      setIsLoading(true);

      if (type === "MOCK_EXAM") {
        const response = await axios.get(
          `${apiUrl}/b2c/practice-exam-attempt/${practice_attempt_id}/analytics`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setExamAttemptAnalytics(response.data);
      } else {
        const response = await axios.get(
          `${apiUrl}/b2c/practice-qset-attempt/${practice_attempt_id}/analytics`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setQsetAttemptAnalytics(response.data);
      }
    } catch (error) {
      toast({ title: "Failed to fetch exam analytics", status: "error", duration: 3000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      if (!isAuthenticated) return;

      setIsInitializing(true);
      setScorePending(false);

      try {
        const contextResponse = await axios.get(
          `${apiUrl}/b2c/practice/assessment-attempt/${practice_attempt_id}/context`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const type = contextResponse.data.assessment_type as "MOCK_EXAM" | "QUESTION_SET";
        setAssessmentType(type);
        setAttemptContext(contextResponse.data);

        const isScorePending = await fetchExamScore(type);

        if (!isScorePending) {
          await fetchExamAnalytics(type);
        }

        await fetchExamAttemptDetails(false);

      } catch {
        toast({ title: "Failed to load attempt", status: "error", duration: 3000, isClosable: true });
      }

      setIsInitializing(false);
    };

    initialize();
  }, [practice_attempt_id]);

// ===== [MY PROGRESS HIGHLIGHT] START =====
// Ensure the sidebar knows which entitlement this attempt belongs to, even
// when arriving via the post-submit redirect (which passes no state).
useEffect(() => {
  if (isAdminView) return;
  const ctxEntitlementId = attemptContext?.entitlement_id;
  if (!ctxEntitlementId) return;
  if (Number((location.state as any)?.entitlement_id) === Number(ctxEntitlementId)) return;
  navigate(location.pathname, { replace: true, state: { entitlement_id: ctxEntitlementId } });
}, [attemptContext]);
// ===== [MY PROGRESS HIGHLIGHT] END =====

  // ── Back to My Progress -- routes to the specific question-set or exam
  //     attempts listing, matching whichever route this student can reach it via ──
const handleBackToProgress = () => {
      if (isAdminView) {
    const id = attemptContext?.entitlement_id;
    navigate(id
      ? `/practice/app/b2c-account-manager/entitlement/${id}/attempts`
      : `/practice/app/b2c-account-manager`);
    return;
  }
  if (!attemptContext?.practice_set_id) return;

    const { practice_set_id, practice_set_exam_id, entitlement_id: entitlementId } = attemptContext;

    if (!entitlementId) {
      navigate(`/practice/app`);
      return;
    }

    if (assessmentType === "MOCK_EXAM" && practice_set_exam_id) {
      navigate(`/practice/app/entitlement/${entitlementId}/practice-set/${practice_set_id}/exam/${practice_set_exam_id}/attempts`);
      return;
    }

    if (assessmentType === "QUESTION_SET") {
      const qsetId = examAttemptDetails?.question_sets?.[0]?.question_set_id;
      if (qsetId) {
        navigate(`/practice/app/entitlement/${entitlementId}/practice-set/${practice_set_id}/practice/0/question-set/${qsetId}/attempts`);
        return;
      }
    }

    navigate(`/practice/app/entitlement/${entitlementId}/practice-set/${practice_set_id}/attempts`);
};

  if (isInitializing) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (scorePending) {
    const isManual = pendingStatus === "MANUAL_SCORING_PENDING";

    return (
      <Box maxW="1400px" mx="auto" p={8}>
        <Box
          bg={isManual ? "blue.50" : "red.50"}
          border="1px solid"
          borderColor={isManual ? "blue.200" : "red.200"}
          borderRadius="lg"
          p={10}
          textAlign="center"
        >
          <Text fontSize="xl" fontWeight="semibold" color={isManual ? "blue.700" : "red.700"} mb={3}>
            {isManual ? "Manual Scoring Pending" : "Scoring Interrupted"}
          </Text>
          <Text fontSize="md" color="gray.600" maxW="600px" mx="auto">
            {pendingMessage ?? (
              isManual
                ? "This attempt is pending manual review. You will receive an email once scoring is complete."
                : "Scoring of this attempt has been interrupted. Please contact your administrator."
            )}
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box maxW="1400px" mx="auto" p={6}>
      <Box mx="auto" color={textColor} p={4}>

        {/* ── HERO: which attempt is this, and when ── */}
        <Box bg={heroBg} border="1px solid" borderColor={heroBorder} borderRadius="2xl" p={6} mb={6}>
          <VStack align="start" spacing={2}>
            <Button
              size="xs"
              variant="ghost"
              leftIcon={<FaArrowLeft />}
              onClick={handleBackToProgress}
              isDisabled={!attemptContext?.practice_set_id}
            >
              {isAdminView ? "Back to Student Progress" : "Back to My Progress"}
            </Button>

            <Text fontSize="2xl" fontWeight="bold">
              {attemptContext?.title ??
                (assessmentType === "MOCK_EXAM" ? "Mock Exam Attempt" : "Question Set Attempt")}
            </Text>

            <HStack spacing={4} fontSize="sm" color={heroMutedText} wrap="wrap">
              {attemptContext?.student_start_time && (
                <HStack spacing={1}>
                  <FaCalendarAlt />
                  <Text>{new Date(attemptContext.student_start_time).toLocaleString()}</Text>
                </HStack>
              )}
              {assessmentType && (
                <Text fontWeight="semibold">
                  {assessmentType === "MOCK_EXAM" ? "Mock Exam" : "Question Set Practice"}
                </Text>
              )}
            </HStack>
          </VStack>
        </Box>

        {/* ── Responsive tab row ── */}
        <SimpleGrid columns={3} spacing={2} mb={6}>
          <Button
            size={{ base: "sm", md: "md" }}
            colorScheme="green"
            leftIcon={<FaTrophy />}
            onClick={() => setViewMode("score")}
            isDisabled={viewMode === "score"}
            variant={viewMode === "score" ? "solid" : "outline"}
          >
            <Box display={{ base: "none", md: "inline" }}>Performance Summary</Box>
            <Box display={{ base: "inline", md: "none" }}>Summary</Box>
          </Button>

          <Tooltip
            label="Analytics not generated yet."
            isDisabled={!!(assessmentType === "MOCK_EXAM" ? examAttemptAnalytics : qsetAttemptAnalytics)}
          >
            <Button
              size={{ base: "sm", md: "md" }}
              colorScheme="purple"
              leftIcon={<FaChartPie />}
              onClick={() => setViewMode("analytics")}
              isDisabled={
                !(assessmentType === "MOCK_EXAM" ? examAttemptAnalytics : qsetAttemptAnalytics) ||
                viewMode === "analytics"
              }
              variant={viewMode === "analytics" ? "solid" : "outline"}
            >
              <Box display={{ base: "none", md: "inline" }}>Topic Performance</Box>
              <Box display={{ base: "inline", md: "none" }}>Topics</Box>
            </Button>
          </Tooltip>

          <Button
            size={{ base: "sm", md: "md" }}
            colorScheme="blue"
            leftIcon={<FaListUl />}
            onClick={() => fetchExamAttemptDetails(true)}
            isDisabled={viewMode === "attempt"}
            variant={viewMode === "attempt" ? "solid" : "outline"}
          >
            <Box display={{ base: "none", md: "inline" }}>Question Review</Box>
            <Box display={{ base: "inline", md: "none" }}>Review</Box>
          </Button>
        </SimpleGrid>

        {/* ── SCORE VIEW ── */}
        {viewMode === "score" && (
          <Box>
            {assessmentType === "MOCK_EXAM" && examAttemptScore && (
              <B2CScoreViewer scoreData={examAttemptScore} />
            )}
            {assessmentType === "QUESTION_SET" && qsetAttemptScore && (
              <B2CQsetScoreViewer attempted_question_set_score={qsetAttemptScore} />
            )}
          </Box>
        )}

        {/* ── ANALYTICS VIEW ── */}
        {isLoading ? (
          <Spinner size="xl" />
        ) : (
          viewMode === "analytics" && (
            <Box>
              {assessmentType === "MOCK_EXAM" ? (
                <>
                  <HStack p={4} spacing={4}>
                    {examAttemptAnalytics?.attempted_question_set_analytics.map((qs, idx) => (
                      <Box
                        key={qs.exam_attempted_question_set_id}
                        p={2}
                        px={4}
                        borderRadius="md"
                        minW="160px"
                        cursor="pointer"
                        bg={selectedIndex === idx ? "blue.600" : cardBgDefault}
                        color={selectedIndex === idx ? "white" : cardTextColor}
                        borderWidth="1px"
                        borderColor={selectedIndex === idx ? "blue.800" : cardBorderColor}
                        onClick={() => setSelectedIndex(idx)}
                        _hover={{
                          bg: selectedIndex === idx ? "blue.700" : cardBgHover,
                          transform: "translateY(-2px)",
                          boxShadow: "md",
                        }}
                        transition="all 0.2s ease"
                        textAlign="center"
                      >
                        <Text fontWeight="semibold" fontSize="sm" isTruncated>
                          {qs.title}
                        </Text>
                      </Box>
                    ))}
                  </HStack>
                  <Divider />
                  <Box position="relative" minH="500px">
                    {examAttemptAnalytics?.attempted_question_set_analytics.map((qs, idx) => (
                      <Box
                        key={qs.exam_attempted_question_set_id}
                        display={selectedIndex === idx ? "block" : "none"}
                        transition="opacity 0.2s ease"
                      >
                        <B2CQuestionSetAnalytics examAttemptedQsetAnalyticsProp={qs} />
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                qsetAttemptAnalytics && (
                  <Box minH="500px">
                    <B2CQuestionSetAnalytics examAttemptedQsetAnalyticsProp={qsetAttemptAnalytics} />
                  </Box>
                )
              )}
            </Box>
          )
        )}

        {/* ── DETAILED ATTEMPT VIEW ── */}
        {isLoading ? (
          <Spinner size="xl" />
        ) : (
          viewMode === "attempt" && (
            <Box>
              {assessmentType === "MOCK_EXAM" ? (
                <>
                  <HStack p={4} spacing={4}>
                    {examAttemptDetails?.question_sets.map((qs, idx) => (
                      <Box
                        key={qs.practice_attempted_question_set_id}
                        p={2}
                        px={4}
                        borderRadius="md"
                        minW="160px"
                        cursor="pointer"
                        bg={selectedIndex === idx ? "blue.600" : cardBgDefault}
                        color={selectedIndex === idx ? "white" : cardTextColor}
                        onClick={() => setSelectedIndex(idx)}
                        _hover={{
                          bg: selectedIndex === idx ? "blue.700" : cardBgHover,
                          transform: "translateY(-2px)",
                        }}
                        transition="all 0.2s ease"
                        textAlign="center"
                      >
                        <Text fontWeight="semibold" fontSize="sm" isTruncated>
                          {qs.question_set_title}
                        </Text>
                        <Text fontSize="xs">
                          Total: {qs.attempted_question_details.length} questions
                        </Text>
                      </Box>
                    ))}
                  </HStack>
                  <Divider />
                  <Box position="relative" minH="500px">
                    {examAttemptDetails?.question_sets.map((qs, idx) => (
                      <Box
                        key={qs.practice_attempted_question_set_id}
                        display={selectedIndex === idx ? "block" : "none"}
                        transition="opacity 0.2s ease"
                      >
                        <B2CQuestionSetViewAttempt examAttemptedQuestionSetProp={qs.attempted_question_details} />
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                examAttemptDetails?.question_sets[0] && (
                  <Box minH="500px">
                    <B2CQuestionSetViewAttempt
                      examAttemptedQuestionSetProp={examAttemptDetails.question_sets[0].attempted_question_details}
                    />
                  </Box>
                )
              )}
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default PracticeAttemptDetailsViewer;